import type { PaletteColor } from "@brexhq/metal-foundation";
import type { Theme } from "@emotion/react";
import styled, { CSSObject } from "@emotion/styled";
import { darken, lighten, margin as polishedMargin } from "polished";
import React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { CoreLink } from "@brexhq/next/src/components/shared/Link/CoreLink";
import { lrgButtons } from "@brexhq/next/src/components/shared/styles/Button.elements";
import useLocation from "@brexhq/next/src/hooks/useLocation";
import ArrowRightIcon from "@brexhq/shared/assets/icons/single-color/ArrowRight";
import ArrowRightBoldIcon from "@brexhq/shared/assets/icons/single-color/ArrowRightBold";
import { type DefaultTheme } from "@brexhq/shared/styles/theming";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { removeTrailingSlash } from "@brexhq/shared/utils/urlFiltering/urlFormatting";

import { IconPicker } from "../../building-blocks/IconPicker/IconPicker";

import type {
  ArrowLinkDirection,
  ArrowLinkProps,
  ArrowLinkVariant,
  ButtonMargins,
  ResponsiveButtonSizes,
} from "./types";

const StyledArrowLink = styled(CoreLink, {
  shouldForwardProp: (prop) =>
    ![
      "margin",
      "color",
      "size",
      "textStyle",
      "mobileTextStyle",
      "direction",
      "withTextAnimation",
      "variant",
    ].includes(prop as string),
})<{
  margin?: ButtonMargins;
  color?: PaletteColor;
  size?: ResponsiveButtonSizes;
  textStyle?: keyof Theme["typography"];
  mobileTextStyle?: keyof Theme["typography"];
  direction: ArrowLinkDirection;
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
}>(
  ({
    theme,
    margin,
    color,
    size = lrgButtons,
    textStyle,
    mobileTextStyle,
    direction,
    withTextAnimation,
  }) => {
    return createArrowLinkStyles({
      theme,
      size,
      margin,
      color,
      textStyle,
      mobileTextStyle,
      direction,
      withTextAnimation,
    });
  },
);

export const StyledArrowAsSpanLink = styled("span")<{
  size?: ResponsiveButtonSizes;
  margin?: ButtonMargins;
  color?: PaletteColor;
  textStyle?: keyof Theme["typography"];
  mobileTextStyle?: keyof Theme["typography"];
  direction?: ArrowLinkDirection;
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
}>(
  ({
    theme,
    size,
    margin,
    color,
    textStyle,
    mobileTextStyle,
    direction = "right",
    withTextAnimation,
    variant,
  }) => {
    return {
      ...createArrowLinkStyles({
        theme,
        size,
        margin,
        color,
        textStyle,
        mobileTextStyle,
        direction,
        withTextAnimation,
        variant,
      }),
    };
  },
);

export const ArrowWrapper = styled.span`
  width: 1.4em;
  width: min-content;
  min-width: 1.4em;
  min-width: min-content;
  display: inline-block;
  vertical-align: baseline;
  line-height: 1;
`;

// Styled wrapper for consistent SVG alignment
const ArrowSvgWrapper = styled.span`
  display: inline-block;
  vertical-align: center;
`;

const RotatedArrowWrapper = styled.span<{ rotation: string }>`
  display: inline-block;
  vertical-align: baseline;
  transform: ${({ rotation }) => `rotate(${rotation})`};
`;

// SVG-based arrow components for consistent rendering
const RightArrow = ({
  color,
  variant = "standard",
}: {
  color?: string;
  variant?: ArrowLinkVariant;
}) => (
  <ArrowSvgWrapper>
    {variant === "bold" ? (
      <ArrowRightBoldIcon color={color || "currentColor"} />
    ) : (
      <ArrowRightIcon color={color || "currentColor"} />
    )}
  </ArrowSvgWrapper>
);

const LeftArrow = ({
  color,
  variant = "standard",
}: {
  color?: string;
  variant?: ArrowLinkVariant;
}) => (
  <RotatedArrowWrapper rotation="180deg">
    {variant === "bold" ? (
      <ArrowRightBoldIcon
        color={color || "currentColor"}
        width="24px"
        height="24px"
      />
    ) : (
      <ArrowRightIcon
        color={color || "currentColor"}
        width="16px"
        height="16px"
      />
    )}
  </RotatedArrowWrapper>
);

const DownArrow = ({
  color,
  variant = "standard",
}: {
  color?: string;
  variant?: ArrowLinkVariant;
}) => (
  <RotatedArrowWrapper rotation="90deg">
    {variant === "bold" ? (
      <ArrowRightBoldIcon
        color={color || "currentColor"}
        width="24px"
        height="24px"
      />
    ) : (
      <ArrowRightIcon
        color={color || "currentColor"}
        width="16px"
        height="16px"
      />
    )}
  </RotatedArrowWrapper>
);

export const arrow = (
  <ArrowWrapper data-name="ArrowWrapper">
    <RightArrow />
  </ArrowWrapper>
);
export const getArrowDirection = (
  to: string,
  location: Window["location"],
): "right" | "down" => {
  if (!to.includes("#")) {
    return "right";
  }
  const justHashString = to.startsWith("#");
  if (justHashString) {
    return "down";
  }
  const { pathname, hostname } = location;

  let link = to;
  if (!link.startsWith("/")) {
    try {
      const url = new URL(to);
      if (url.hostname === hostname) {
        link = url.pathname;
      }
    } catch (error) {
      console.error("Invalid URL passed to `new URL()`:", to);
    }
  }

  const pathWithNoHash = removeTrailingSlash(link.split("#")[0]);
  const urlPathWithNoHash = removeTrailingSlash(pathname.split("#")[0]);

  if (pathWithNoHash === urlPathWithNoHash) {
    return "down";
  }
  // default - should only be run if hash linking to a seperate page
  return "right";
};

export const ArrowLink = React.forwardRef<HTMLAnchorElement, ArrowLinkProps>(
  function ArrowLinkComponent(
    {
      label,
      to,
      preventDefault,
      size,
      margin,
      color,
      target,
      rel,
      prefetch,
      direction = "right",
      withTextAnimation = false,
      variant = "standard",
      icon,
      withIcon,
      iconColor,
      theme,
      ...defaultProps
    },
    ref,
  ) {
    const shouldPointDown = getArrowDirection(to, useLocation()) === "down";

    const getArrowComponent = () => {
      if (shouldPointDown) {
        return <DownArrow color={color ?? "currentColor"} variant={variant} />;
      }
      return direction === "left" ? (
        <LeftArrow color={color ?? "currentColor"} variant={variant} />
      ) : (
        <RightArrow color={color ?? "currentColor"} variant={variant} />
      );
    };

    const internalLink = () => (
      <Container
        display={icon && withIcon ? "flex" : undefined}
        gap={icon && withIcon ? "xxsml" : undefined}
        alignItems={icon && withIcon ? "center" : undefined}
      >
        {direction === "left" && (
          <>
            {icon && withIcon && (
              <IconPicker
                theme={theme as ModuleThemeType}
                marginTop="none"
                size={{ LRG: "med", MED: "med", SML: "sml" }}
                icon={icon}
                color={iconColor ?? "single"}
              />
            )}
            <ArrowWrapper data-name="ArrowWrapper">
              {getArrowComponent()}
            </ArrowWrapper>
            {withTextAnimation ? (
              <span className="label-wrapper">
                <span className="label-text">{label}</span>
              </span>
            ) : (
              <>
                {variant !== "bold" && <>&nbsp;</>}
                {label}
              </>
            )}
          </>
        )}
        {direction === "right" && (
          <>
            {icon && withIcon && (
              <IconPicker
                theme={theme as ModuleThemeType}
                marginTop="none"
                size={{ LRG: "med", MED: "med", SML: "sml" }}
                icon={icon}
                color={iconColor ?? "single"}
              />
            )}
            {withTextAnimation ? (
              <span className="label-wrapper">
                <span className="label-text">{label}</span>
              </span>
            ) : (
              <>
                {label}
                {variant !== "bold" && <>&nbsp;</>}
              </>
            )}
            <ArrowWrapper data-name="ArrowWrapper">
              {getArrowComponent()}
            </ArrowWrapper>
          </>
        )}
      </Container>
    );

    if (preventDefault) {
      return (
        <StyledArrowAsSpanLink
          ref={ref}
          size={size}
          margin={margin}
          color={color}
          direction={direction}
          withTextAnimation={withTextAnimation}
          variant={variant}
          {...defaultProps}
        >
          {internalLink()}
        </StyledArrowAsSpanLink>
      );
    }

    return (
      <StyledArrowLink
        ref={ref}
        to={to}
        margin={margin}
        color={color}
        target={shouldPointDown ? "_self" : target}
        size={size}
        rel={rel}
        prefetch={prefetch}
        direction={direction}
        withTextAnimation={withTextAnimation}
        variant={variant}
        {...defaultProps}
      >
        {internalLink()}
      </StyledArrowLink>
    );
  },
);

function createArrowLinkStyles({
  theme,
  size = lrgButtons,
  margin = [0, 0, 0, 0],
  color,
  textStyle,
  mobileTextStyle,
  direction = "right",
  withTextAnimation = false,
  variant = "standard",
}: {
  theme: DefaultTheme;
  size?: ResponsiveButtonSizes;
  margin?: ButtonMargins;
  color?: PaletteColor;
  textStyle?: keyof Theme["typography"];
  mobileTextStyle?: keyof Theme["typography"];
  direction: ArrowLinkDirection;
  withTextAnimation?: boolean;
  variant?: ArrowLinkVariant;
}): CSSObject {
  const arrowColor = color ?? theme.colors.accent.primary.background.default;
  const lrgSize =
    size.LRG === "lrg"
      ? "16px"
      : size.LRG === "DEVIATED_NAV_SIZE"
      ? "14px"
      : "12px";
  const smlSize =
    size.SML === "lrg"
      ? "14px"
      : size.SML === "DEVIATED_NAV_SIZE"
      ? "14px"
      : "12px";
  const transform =
    direction === "right" ? "translateX(4px)" : "translateX(-4px)";

  const lrgLineHeight = size.LRG === "DEVIATED_NAV_SIZE" ? "20px" : "24px";
  const smlLineHeight = size.SML === "DEVIATED_NAV_SIZE" ? "20px" : "22px";

  const baseStyles: CSSObject = {
    display: withTextAnimation ? "inline-flex" : "inline-block",
    alignItems: withTextAnimation ? "center" : undefined,
    fontFamily: theme.rebirth.fonts.family,
    color: arrowColor,
    fontSize: lrgSize,
    lineHeight: lrgLineHeight,
    fontVariationSettings: theme.fonts.FontWeight.MEDIUM,
    ...(variant === "bold" && {
      fontSize: "24px",
      lineHeight: "26.4px",
      fontWeight: "500",
    }),
    ...(textStyle && {
      fontVariationSettings: "unset",
      ...theme.typography[textStyle],
    }),
    textDecoration: "none",
    ...polishedMargin(...margin),
    "span[data-name='ArrowWrapper']": {
      display: "inline-block",
      verticalAlign: "baseline",
      lineHeight: "1",
      transition: "all .125s linear",
      transform: "translateX(0px)",
      paddingLeft: withTextAnimation
        ? direction === "left"
          ? "0"
          : "8px"
        : theme.spacing.m4,
      paddingRight:
        withTextAnimation && direction === "left" ? "8px" : undefined,
      // Add extra padding to accommodate arrow movement when withTextAnimation is true
      ...(withTextAnimation && {
        paddingLeft: direction === "left" ? "6px" : "0",
        paddingRight: direction === "right" ? "6px" : "0",
      }),
    },
    "&:hover": {
      ...(withTextAnimation ? {} : { color: darken(0.2, arrowColor) }),
      textDecoration: "none",
      "span[data-name='ArrowWrapper']": {
        transform,
      },
    },
    "&:active": {
      color: lighten(0.2, arrowColor),
      textDecoration: "none",
    },
    "&:visited": {
      color: arrowColor,
      textDecoration: "none",
    },
    [theme.breakpoints.SML]: {
      fontSize: smlSize,
      lineHeight: smlLineHeight,
      ...(mobileTextStyle && theme.typography[mobileTextStyle]),
    },
  };

  // Add text animation styles when withTextAnimation is true
  if (withTextAnimation) {
    baseStyles[".label-wrapper"] = {
      overflow: "hidden",
      height: "1.2em",
      display: "inline-flex",
      alignItems: "flex-end",
    };

    baseStyles[".label-text"] = {
      display: "inline-block",
      transition: "transform 0.3s ease-out",
      transform: "translateY(100%)",
      whiteSpace: "nowrap",
    };

    // Only apply hover animation on devices that support hover (desktop)
    baseStyles["@media (hover: hover)"] = {
      "&:hover .label-text": {
        transform: "translateY(0)",
      },
    };
  }

  return baseStyles;
}
